import { useCallback, useContext, useEffect, useState } from 'react';

import { ProductVariantUID, ProductWidget, SizeRecommendationRequestResultProps, userDataCompleted } from '@vestico/model';

import { UserContext } from '@vestico/shared';

import { MatchingAPIContext, WidgetAuthContext } from '../MainContext';

export interface useSizeRecommendationsProps {
  product: ProductWidget | null | undefined;
  selectedVariantUID?: ProductVariantUID;
  enabled: boolean;
  force?: boolean;
}

export const useSizeRecommendations = ({ product, selectedVariantUID, enabled, force }: useSizeRecommendationsProps) => {
  const api = useContext(MatchingAPIContext);

  const { user, uid: userUID } = useContext(UserContext);
  const { authInfo, resetAuth } = useContext(WidgetAuthContext);

  const [sizeRecommendationResult, setSizeRecommendationResult] = useState<SizeRecommendationRequestResultProps | undefined | null>(undefined);
  const [loadingSizeRecs, setLoadingSizeRecs] = useState(false);

  const loadSizeRecommendationIfNeeded = useCallback(
    async (force?: boolean) => {
      if (!enabled || !product || !user || !userUID || !userDataCompleted(user, product?.matchingCategory, false, true)) {
        setSizeRecommendationResult(undefined);
        return;
      }

      if (!force) {
        return;
      }

      if (sizeRecommendationResult !== undefined) {
        return;
      }

      if (loadingSizeRecs || !api) {
        return;
      }

      setLoadingSizeRecs(true);
      console.debug('Start fetching size recommendation.');

      const result = await api.fetchSizeRecommendations({
        product,
        selectedVariantUID,
        user,
        authRequestInfo: { authInfo, resetAuth },
      });

      setSizeRecommendationResult(result ?? null);

      setLoadingSizeRecs(false);
    },
    [enabled, product, user, userUID, sizeRecommendationResult, loadingSizeRecs, api, selectedVariantUID, authInfo, resetAuth]
  );

  useEffect(() => {
    loadSizeRecommendationIfNeeded(force);
  }, [force, loadSizeRecommendationIfNeeded]);

  useEffect(() => {
    setSizeRecommendationResult(undefined);
  }, [selectedVariantUID, product]);

  return { sizeRecommendationResult: sizeRecommendationResult ?? undefined };
};
